import { useState, useEffect, useRef } from 'react';
import { useStepNavigation } from '../../hooks/useStepNavigation';
import StepIndicator from '../../components/StepIndicator';
import StepNavigation from '../../components/StepNavigation';
import DateSelection from '../../components/DateSelection';
import { clinicasService } from '../../services/clinicas.service';
import PageContainer from '../../components/PageContainer';
import PageHeader from '../../components/PageHeader';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { limparRascunhoAgendamento } from '../../utils/agendamentoStorage';

interface ClinicaInfo {
  id: number;
  nome: string;
}

interface Colaborador {
  id: string;
  nome: string;
  cpf: string;
  clinica_id?: number;
  clinicaNome?: string;
  dataAgendamento?: string;
  exames?: unknown[];
}

export default function AgendamentoPage() {
  const { empresa } = useEmpresa();
  const { currentStep, stepData, saveStepData, goToNextStep, goToPreviousStep } = useStepNavigation();
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [clinicasMap, setClinicasMap] = useState<Map<number, ClinicaInfo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const prevEmpresaIdRef = useRef<number | null>(empresa?.id ?? null);

  useEffect(() => {
    if (!empresa?.id) return;
    const currentEmpresaId = empresa.id.toString();
    const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');

    const empresaMudou = prevEmpresaIdRef.current !== null && prevEmpresaIdRef.current !== empresa.id;
    const rascunhoDivergente = Boolean(savedEmpresaId && savedEmpresaId !== currentEmpresaId);

    if (empresaMudou || rascunhoDivergente) {
      console.log('🔄 [Agendamento] Empresa alterada durante agendamento. Limpando rascunho e redirecionando para início.');
      limparRascunhoAgendamento();
      window.location.href = '/';
      return;
    }
    prevEmpresaIdRef.current = empresa.id;
  }, [empresa?.id]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const savedEmpresaId = localStorage.getItem('customer_employeesData_empresa_id');
        const currentEmpresaId = empresa?.id ? empresa.id.toString() : localStorage.getItem('customer_empresa_id');

        if (savedEmpresaId && currentEmpresaId && savedEmpresaId !== currentEmpresaId) {
          console.warn('⚠️ [Agendamento] Empresa divergente. Limpando rascunho e redirecionando.');
          limparRascunhoAgendamento();
          window.location.href = '/';
          return;
        }

        // Carregar colaboradores do stepData
        if (!stepData.employeesData || stepData.employeesData.length === 0) {
          window.location.href = '/agendamento/cadastro';
          return;
        }

        // Carregar informações das clínicas
        const colaboradoresComClinicas = await Promise.all(
          stepData.employeesData.map(async (col: Record<string, unknown>) => {
            try {
              // Se já tem clínica selecionada, buscar dados da clínica
              if (col.clinica) {
                const clinicaId = parseInt(String(col.clinica), 10);
                const clinica = await clinicasService.getClinicaById(clinicaId);
                return {
                  ...col,
                  clinica_id: clinicaId,
                  clinicaNome: clinica.nome,
                } as unknown as Colaborador;
              }
              return col as unknown as Colaborador;
            } catch (err) {
              console.error(`Erro ao carregar clínica do colaborador ${col.id}:`, err);
              return col as unknown as Colaborador;
            }
          })
        );

      setColaboradores(colaboradoresComClinicas);

      // Criar mapa de clínicas únicas
      const clinicasUnicas = new Map<number, ClinicaInfo>();
      colaboradoresComClinicas.forEach(col => {
        if (col.clinica_id && !clinicasUnicas.has(col.clinica_id)) {
          clinicasUnicas.set(col.clinica_id, {
            id: col.clinica_id,
            nome: col.clinicaNome || 'Clínica não especificada'
          });
        }
      });
      setClinicasMap(clinicasUnicas);

    } catch (err: unknown) {
      console.error('Erro ao carregar dados:', err);
      const e = err as { message?: string };
      setError(e?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

    loadInitialData();
  }, [stepData.employeesData, empresa?.id]);

  const handleDateSelect = (colaboradorId: string, selectedDate: string) => {
    setColaboradores(prev =>
      prev.map(col =>
        col.id === colaboradorId
          ? { ...col, dataAgendamento: selectedDate }
          : col
      )
    );
  };

  const handleContinuar = async () => {
    setSaving(true);
    setError('');
    try {
      // Salvar dados de agendamento no stepData
      const schedulingData = colaboradores
        .filter(col => col.dataAgendamento && col.clinica_id)
        .map(col => ({
          colaborador_id: parseInt(col.id),
          data_agendamento: col.dataAgendamento!,
          clinica_id: col.clinica_id!
        }));

      saveStepData({ schedulingData });

      // Atualizar employeesData com as datas selecionadas
      const updatedEmployeesData = stepData.employeesData?.map((emp: Record<string, unknown>) => {
        const colaborador = colaboradores.find(c => c.id === emp.id);
        return {
          ...emp,
          dataAgendamento: colaborador?.dataAgendamento || emp.dataAgendamento,
          clinica_id: colaborador?.clinica_id || emp.clinica,
          clinicaNome: colaborador?.clinicaNome || emp.clinicaNome,
        };
      });

      saveStepData({ employeesData: updatedEmployeesData });

      console.log('✅ Agendamentos atualizados e dados salvos');

      // Avançar para o próximo passo
      goToNextStep();
    } catch (err: unknown) {
      console.error('❌ Erro ao salvar agendamentos:', err);
      const apiErr = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = apiErr?.response?.data?.message || apiErr?.message || 'Erro ao salvar agendamentos';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getColaboradoresPorClinica = (clinicaId: number) => {
    return colaboradores.filter(col => col.clinica_id === clinicaId);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Agendamento de Exames"
        description="Selecione as datas disponíveis para cada colaborador realizar seus exames"
      />

        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Step navigation */}
        <StepNavigation
          currentStep={currentStep}
          canGoNext={colaboradores.every(col => col.dataAgendamento)}
          canGoPrevious={true}
          onNext={handleContinuar}
          onPrevious={goToPreviousStep}
          loading={saving}
        />

        {/* Informações adicionais */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            💡 Sobre o agendamento
          </h3>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>• As datas disponíveis consideram o horário de funcionamento de cada clínica</li>
            <li>• Feriados e recessos não estão disponíveis para agendamento</li>
            <li>• Cada colaborador deve ter sua data selecionada antes de continuar</li>
            <li>• Você pode alterar as clínicas posteriormente no resumo se necessário</li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Erro na operação
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p className="whitespace-pre-line">{error}</p>
                </div>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                >
                  <span className="sr-only">Fechar</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista de clínicas e colaboradores */}
        <div className="space-y-8">
          {Array.from(clinicasMap.values()).map((clinica) => {
            const colaboradoresDaClinica = getColaboradoresPorClinica(clinica.id);

            return (
              <div key={clinica.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900">
                    Clínica: {clinica.nome}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    {colaboradoresDaClinica.length} colaborador(es) para agendar
                  </p>
                </div>

                {/* Seletor de datas da clínica */}
                <div className="p-6 border-b border-gray-200">
                  <DateSelection
                    clinicaId={clinica.id}
                    onDateSelect={(date) => {
                      // Quando uma data é selecionada na clínica, aplicar para todos os colaboradores
                      colaboradoresDaClinica.forEach(col => {
                        handleDateSelect(col.id, date);
                      });
                    }}
                  />
                </div>

                {/* Lista de colaboradores da clínica */}
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Colaboradores nesta clínica
                  </h4>
                  <div className="space-y-4">
                    {colaboradoresDaClinica.map((colaborador) => (
                      <div key={colaborador.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h5 className="font-semibold text-gray-900">{colaborador.nome}</h5>
                          <p className="text-sm text-gray-600">CPF: {colaborador.cpf}</p>
                          {colaborador.dataAgendamento && (
                            <p className="text-sm text-green-600 font-medium mt-1">
                              ✅ Agendado para: {colaborador.dataAgendamento}
                            </p>
                          )}
                        </div>
                        {!colaborador.dataAgendamento && (
                          <div className="text-sm text-orange-600">
                            Data não selecionada
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
    </PageContainer>
  );
}


